import {
    DapiRequestManagerBase,
    DapiRequestManagerBaseOptions,
    sendRequest,
} from './DapiRequestManagerBase';
import { buildLogger, timeUtils } from '@instinet-analytics/core-lib';
import { v4 as uuid } from 'uuid';
import {
    BarTickData,
    BloombergEvent,
    EventTypes,
    IntraDayBarRequest,
    IntraDayEventType,
    ReferenceDataRequest,
    ReferenceDataRequestArguments,
    SecurityData,
} from '@glue42/bbg-market-data';
import { getMarketBarMessageData } from './types/MarketBarMessage';
import { BarData } from 'marketData/store/marketDataSlice';
import {
    BarDataReceivedHandler,
    RefDataReceivedHandler,
} from 'marketData/ExternalMarketDataStrategy';
import {
    addRequest,
    recordMessage,
    recordStatus,
} from 'app/glue/bloomberg/store/bloombergSlice';
import {
    parseBloombergDateTime,
    toDateTimeStr,
} from 'app/glue/bloomberg/requests/utils';
import {
    BloombergMarketDataRequest,
    MarketDataRequestBarRequest,
} from 'marketData/MarketDataRequest';

const { addDays, roundToDays } = timeUtils;

type BarDataAlertHandler = (
    request: MarketDataRequestBarRequest & BloombergMarketDataRequest,
    message: string
) => void;

type Options = DapiRequestManagerBaseOptions & {
    onBarDataReceived: BarDataReceivedHandler;
    onRefDataReceived: RefDataReceivedHandler;
    onBarDataMessage: BarDataAlertHandler;
};
/*
 * Kicks of a mktbar real time bar request
 * Also fetches the start and end session time for the ref data, and if the session start is before now, retrieves the historical bars for today
 */
export class DapiBarDataRequestManager extends DapiRequestManagerBase {
    private currentSubscriptionUid: string | undefined;
    private readonly onBarDataReceived: BarDataReceivedHandler;
    private readonly onRefDataReceived: RefDataReceivedHandler;
    private readonly onBarDataMessage: BarDataAlertHandler;

    private historicalRequest: IntraDayBarRequest | undefined;

    public constructor(
        public request: MarketDataRequestBarRequest &
            BloombergMarketDataRequest,
        options: Options
    ) {
        const logger = buildLogger(
            'BloombergMarketDataBarRequest',
            options.loggingService
        );
        super(request, logger, options);

        this.onBarDataReceived = options.onBarDataReceived;
        this.onBarDataMessage = options.onBarDataMessage;
        this.onRefDataReceived = options.onRefDataReceived;
    }

    private get currentSubscriptionInteropId() {
        return `Indigo-DapiBarSub-${this.currentSubscriptionUid}`;
    }

    public subscribe() {
        this.currentSubscriptionUid = uuid();

        this.glueInteropApi
            .register(
                this.currentSubscriptionInteropId,
                this.onRealTimeInteropCallback.bind(this)
            )
            .then(() => {
                this.logger.debug(
                    'Registered interop callback ' +
                        this.currentSubscriptionInteropId
                );
            })
            .catch((err) => {
                this.logger.critical(
                    'Error registering interop callback ' +
                        this.currentSubscriptionInteropId,
                    err
                );
            });

        try {
            this.logger.debug('Subscribing...', {
                interopCallbackId: this.currentSubscriptionInteropId,
                request: this.request,
            });

            this.getRefData();
            this.subscribeHistorical();

            this.subscribeRealtime()
                .then(() => {
                    this.logger.debug('Invoked bar requests', {
                        interopCallbackId: this.currentSubscriptionInteropId,
                        request: this.request,
                    });
                })
                .catch((err) => {
                    this.logger.debug('Error invoking bar requests', {
                        interopCallbackId: this.currentSubscriptionInteropId,
                        request: this.request,
                        error: err,
                    });
                });
        } catch (err) {
            this.logger.critical('Error subscribing for market data', err);
            this.onBarDataMessage(
                this.request,
                'Error subscribing for market data'
            );
        }
    }

    private getRefData() {
        const args: ReferenceDataRequestArguments = {
            securities: [this.security],
            fields: [
                'TRADING_DAY_START_TIME_EOD',
                'TRADING_DAY_END_TIME_EOD',
                'VOLUME_AVG_20D',
                'PREV_CLOSE_VAL',
            ],
            useUTCTime: true,
        };

        const referenceDataRequest: ReferenceDataRequest =
            this.mktDataApi.createReferenceDataRequest(args);

        this.dispatch(
            addRequest({
                uid: this.currentSubscriptionUid!,
                requestType: 'ReferenceDataRequest',
                request: this.request,
                args,
            })
        );

        sendRequest(referenceDataRequest, {
            requestType: 'ReferenceDataRequest',
            dispatcher: this.dispatcher,
            logger: this.logger,
            uid: this.currentSubscriptionUid!,
            onResponseData: (d) => this.handleRefDataReceived(d),
        });
    }

    private subscribeHistorical() {
        const startDateTime = toDateTimeStr(
            new Date(addDays(new Date().getTime(), -2))
        );
        const endDateTime = toDateTimeStr(
            new Date(addDays(roundToDays(new Date().getTime()), 2) - 1)
        );

        const args = {
            security: this.security,
            eventType: IntraDayEventType.TRADE,
            startDateTime,
            endDateTime,
            gapFillInitialBar: true,
            interval: this.request.barSize,
        };

        this.dispatch(
            addRequest({
                uid: this.currentSubscriptionUid!,
                requestType: 'IntraDayBarRequest',
                request: this.request,
                args,
            })
        );

        this.historicalRequest = this.mktDataApi.createIntraDayBarRequest(args);

        sendRequest(this.historicalRequest, {
            requestType: 'IntraDayBarRequest',
            dispatcher: this.dispatcher,
            logger: this.logger,
            uid: this.currentSubscriptionUid!,
            onResponseData: (d) => this.handleHistoricalBars(d.barTickData),
        });
    }

    private get security() {
        return `${this.request.bbSymbol} Equity`;
    }

    private async subscribeRealtime() {
        const subscription = {
            subscriptionId: this.currentSubscriptionUid,
            security: `//blp/mktbar/ticker/${this.security}`,
            fields: 'LAST_PRICE',
            options: 'bar_size=5',
        };

        this.dispatch(
            addRequest({
                uid: this.currentSubscriptionUid!,
                requestType: 'mktbar',
                request: this.request,
                args: subscription,
            })
        );

        try {
            const result = await this.glueInteropApi.invoke(
                'T42.MDFApi.CreateSubscriptionRequest',
                {
                    requestCorrelationId: this.currentSubscriptionUid!,
                    settings: {
                        sessionOptions: {
                            serverHost: 'localhost',
                            serverPort: 8194,
                        },
                        sessionName: '',
                    },
                    subscriptions: [subscription],
                    callbackMethod: this.currentSubscriptionInteropId,
                }
            );

            this.logger.debug('Subscribed to Bloomberg', {
                interopCallbackId: this.currentSubscriptionInteropId,
                request: this.request,
                invocationResult: result,
            });

            return result;
        } catch (err) {
            this.logger.critical('Unable to subscribe to Bloomberg', {
                interopCallbackId: this.currentSubscriptionInteropId,
                request: this.request,
                error: err,
            });
        }
    }

    private logMktBarStatus(msg: BloombergEvent): { handledStatus: boolean } {
        switch (msg.eventType) {
            case EventTypes.SessionStatus:
            case EventTypes.ServiceStatus:
            case EventTypes.SubscriptionStatus: {
                const statuses = msg.eventMessages
                    .map((em) => Object.keys(em)[0])
                    .join(', ');

                this.logger.debug(`mktbar ${msg.eventType} = ${statuses}`, msg);

                this.dispatch(
                    recordStatus({
                        uid: this.currentSubscriptionUid!,
                        requestType: 'mktbar',
                        eventType: msg.eventType,
                        status: statuses,
                    })
                );

                return { handledStatus: true };
            }
        }

        return { handledStatus: false };
    }

    public onRealTimeInteropCallback(event: { msg: BloombergEvent }) {
        const { msg } = event;

        const { handledStatus } = this.logMktBarStatus(msg);
        if (handledStatus) {
            return;
        }

        this.dispatch(
            recordMessage({
                uid: this.currentSubscriptionUid!,
                requestType: 'mktbar',
                eventType: event.msg.eventType,
                response: event,
            })
        );

        switch (event.msg.eventType) {
            case EventTypes.SubscriptionData: {
                for (const message of event.msg.eventMessages) {
                    this.handleMessage(message);
                }
                return;
            }
            default:
                this.logger.warn('Ignoring mktbar message', {
                    interopCallbackId: this.currentSubscriptionInteropId,
                    request: this.request,
                    event,
                });
        }
    }

    private handleMessage(message: { [p: string]: unknown }) {
        const data = getMarketBarMessageData(message);

        if (!data) {
            this.logger.debug('Ignoring unrecognised message structure', {
                interopCallbackId: this.currentSubscriptionInteropId,
                request: this.request,
                message,
            });
            return;
        }

        this.onBarDataReceived([
            {
                symbol: this.request.symbol,
                time: parseBloombergDateTime(data.DATE_TIME),
                binSize: this.request.barSize,
                open: data.OPEN,
                close: data.CLOSE,
                high: data.HIGH,
                low: data.LOW,
                volume: data.VOLUME,
            },
        ]);
    }

    private handleHistoricalBars(bars: BarTickData[]) {
        this.logger.debug('Handling IntraDayBarRequest historical bars', {
            interopCallbackId: this.currentSubscriptionInteropId,
            request: this.request,
            bars,
        });

        const barData: BarData[] = bars.map((bar) => ({
            symbol: this.request.symbol,
            time: parseBloombergDateTime(bar.time),
            binSize: this.request.barSize,
            open: bar.open,
            close: bar.close,
            high: bar.high,
            low: bar.low,
            volume: bar.volume,
        }));

        this.onBarDataReceived(barData);
    }

    private handleRefDataReceived(data: SecurityData[]) {
        const securityRefData = data.find((d) => d.security === this.security);
        if (securityRefData) {
            const refData = securityRefData.fieldData as {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                TRADING_DAY_START_TIME_EOD?: string;
                // eslint-disable-next-line @typescript-eslint/naming-convention
                TRADING_DAY_END_TIME_EOD?: string;
                // eslint-disable-next-line @typescript-eslint/naming-convention
                VOLUME_AVG_20D?: number;
                // eslint-disable-next-line @typescript-eslint/naming-convention
                PREV_CLOSE_VAL?: number;
            };
            if (
                refData.TRADING_DAY_START_TIME_EOD &&
                refData.TRADING_DAY_END_TIME_EOD
            ) {
                const start = new Date(
                    parseBloombergDateTime(refData.TRADING_DAY_START_TIME_EOD)
                );
                const end = new Date(
                    parseBloombergDateTime(refData.TRADING_DAY_END_TIME_EOD)
                );
                this.logger.debug('Received ref data', {
                    start,
                    startRaw: refData.TRADING_DAY_START_TIME_EOD,
                    end,
                    endRaw: refData.TRADING_DAY_END_TIME_EOD,
                    averageVolume: refData.VOLUME_AVG_20D,
                    pnc: refData.PREV_CLOSE_VAL,
                });

                this.onRefDataReceived({
                    bbSymbol: this.request.symbol,
                    startTradingHoursTimestamp: start.getTime(),
                    endTradingHoursTimestamp: end.getTime(),
                    averageVolume: refData.VOLUME_AVG_20D ?? 0,
                    pnc: refData.PREV_CLOSE_VAL ?? 0,
                });

                return;
            }
        }

        this.logger.warn('Did not receive ref data', data);
    }

    protected async closeRequest() {
        this.logger.debug('Closing...', {
            interopCallbackId: this.currentSubscriptionInteropId,
            request: this.request,
        });
        this.status = 'Complete';
        try {
            const result = await this.glueInteropApi.invoke(
                'T42.MDFApi.CancelRequests',
                { requestCorrelationIds: [this.currentSubscriptionUid] }
            );
            this.logger.debug('T42.MDFApi.CancelRequests', {
                interopCallbackId: this.currentSubscriptionInteropId,
                request: this.request,
                invocationResult: result,
            });
            if (this.historicalRequest) {
                await this.historicalRequest.close();
            }
        } catch (err) {
            this.logger.warn('Unable to unsubscribe from Bloomberg', {
                interopCallbackId: this.currentSubscriptionInteropId,
                request: this.request,
                error: err,
            });
        }
    }
}
